/**
 * NDJSON record construction — the single owner of [log-format.md](../docs/log-format.md)
 * schema 1. Builds the record objects the store persists and the download serialises;
 * it does not touch storage, the DOM, or the clock (the clock is injected, so the
 * output is deterministic and testable).
 *
 * The `seq` counter lives here and is the only one (REQ-LOG-2): every record passes
 * through this recorder, so `seq` is gap-free by construction rather than discipline.
 * Null-valued GPS fields are omitted, never zeroed — a zero is indistinguishable from a
 * real reading and cannot be recovered afterwards (REQ-GPS-2).
 */

/** Injected time source. `now` for wall-clock `t`, `mono` for the monotonic `m`. */
export type Clock = { now(): Date; mono(): number };

/** CH340 identity for the meta record, as decimal integers (log-format §3). */
export type DeviceInfo = { vid: number; pid: number; ver: number };

export type RecordKind = 'meta' | 'line' | 'tx' | 'fix' | 'err';

/**
 * Who issued a `tx` line (log-format §3). `remote` arrived with the remote console (02 §3.5) and
 * did **not** bump `schema`: `logger.py` dispatches on `k` and carries `src` through rather than
 * matching on it, which is the condition log-format §6 sets for widening an enum in place.
 *
 * It names the *issuer*, not the bytes — `v` is always the text actually written to the board, so
 * a remote `cfg` whose `pwr=` was clamped records the clamped line.
 */
export type TxSource = 'macro' | 'typed' | 'remote';

type Base = { seq: number; t: string; m: number; k: RecordKind };

export type MetaRecord = Base & {
  k: 'meta';
  schema: 1;
  session: string;
  build: string;
  ua: string;
  baud: number;
  dev?: DeviceInfo;
};
export type LineRecord = Base & { k: 'line'; v: string };
export type TxRecord = Base & { k: 'tx'; v: string; src: TxSource };
export type FixRecord = Base & {
  k: 'fix';
  lat: number;
  lon: number;
  acc: number;
  gt: number;
  alt?: number;
  alt_acc?: number;
  spd?: number;
  hdg?: number;
};
export type ErrRecord = Base & { k: 'err'; w: 'usb' | 'gps' | 'store' | 'page'; v: string };

export type LogRecord = MetaRecord | LineRecord | TxRecord | FixRecord | ErrRecord;

export type Recorder = {
  meta(dev: DeviceInfo | null, baud: number): MetaRecord;
  line(v: string): LineRecord;
  tx(v: string, src: TxSource): TxRecord;
  fix(p: GeolocationPosition): FixRecord;
  err(w: ErrRecord['w'], v: string): ErrRecord;
};

/**
 * `ua` is a constructor argument rather than read from `navigator`, so the recorder
 * stays free of DOM globals and remains unit-testable (REQ-NFR-4).
 */
export function createRecorder(
  session: string,
  build: string,
  ua: string,
  clock: Clock,
): Recorder {
  let seq = 0;

  const base = (k: RecordKind): Base => ({
    seq: seq++,
    t: clock.now().toISOString(),
    m: clock.mono(),
    k,
  });

  return {
    meta(dev, baud) {
      const r: MetaRecord = { ...base('meta'), k: 'meta', schema: 1, session, build, ua, baud };
      if (dev) r.dev = { vid: dev.vid, pid: dev.pid, ver: dev.ver };
      return r;
    },
    line(v) {
      return { ...base('line'), k: 'line', v };
    },
    tx(v, src) {
      return { ...base('tx'), k: 'tx', v, src };
    },
    fix(p) {
      const c = p.coords;
      const r: FixRecord = {
        ...base('fix'),
        k: 'fix',
        lat: c.latitude,
        lon: c.longitude,
        acc: c.accuracy,
        gt: p.timestamp,
      };
      if (c.altitude != null) r.alt = c.altitude;
      if (c.altitudeAccuracy != null) r.alt_acc = c.altitudeAccuracy;
      if (c.speed != null) r.spd = c.speed;
      if (c.heading != null) r.hdg = c.heading;
      return r;
    },
    err(w, v) {
      return { ...base('err'), k: 'err', w, v };
    },
  };
}
